<script setup>
import { onBeforeMount, onMounted, onUnmounted } from 'vue'
import { useMainStore } from '@/stores/main'
import Slideover from '@/components/SlideOver/slideover.vue'
import modal from '@/components/modals/modal.vue'
import MOTDToast from '@/components/MOTDToast.vue'
import ScratchpadEditor from '@/components/SlideOver/forms/ScratchpadEditor.vue'
import GuideOverlay from '@/guides/GuideOverlay.vue'
import Sortable, { MultiDrag, Swap } from 'sortablejs';

let mainStore = useMainStore()

onBeforeMount( async () => {
  window.Sortable = Sortable;
  await mainStore.init()
})

// Global keyboard shortcuts
function handleGlobalKeydown(event) {
  // ESC key - Close any open slideover (issue #163)
  if (event.key === 'Escape' && mainStore.slideover_show) {
    event.preventDefault()
    mainStore.close_slideover()
    return
  }

  // Ctrl+. / Cmd+. - Toggle scratchpad
  if ((event.ctrlKey || event.metaKey) && event.key === '.') {
    const currentSlideover = mainStore.slideover_history[mainStore.slideover_index]
    const scratchpadOpen = mainStore.slideover_show && currentSlideover?.name === 'Scratchpad'

    // Always allow closing the scratchpad, but block opening when in input/textarea/contenteditable
    if (!scratchpadOpen) {
      const activeEl = document.activeElement
      const tagName = activeEl?.tagName?.toLowerCase()
      if (tagName === 'input' || tagName === 'textarea' || activeEl?.isContentEditable) {
        return
      }
    }

    event.preventDefault()

    // Toggle scratchpad
    if (scratchpadOpen) {
      mainStore.close_slideover()
    } else {
      mainStore.show_slideover(ScratchpadEditor, {}, { width: 50 }, 'Scratchpad')
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
})
</script>

<template>
  <div style="height: 100%;" ondragstart="return false;">
    <slideover :show="mainStore.slideover_show && mainStore.slideover_index >= 0" :component="mainStore.slideover_history[mainStore.slideover_index]" @slideover-close="mainStore.close_slideover" :options="mainStore.slideover_options"></slideover>
    <modal :show="mainStore.modal_show" :component="mainStore.modal_component" :properties="mainStore.modal_props" @modal-close="mainStore.close_modal()" :fullscreen="mainStore.modal_fullscreen"></modal>
    <MOTDToast />
    <GuideOverlay />
    <router-view/>
  </div>
</template>

<style>
/*@import url('datatables.net-dt');*/
/*@import url('datatables.net-fixedcolumns-dt');*/

:root {
  --free-plan: rgb(246 211 129);
  --basic-plan: rgb(246 129 237);
  --pro-plan: rgb(141 129 246);
  --elite-plan: rgb(129 186 246);
  --enterprise-plan: #FFFFFF;
  --danger: #9E4A4A; 
  --warning: #E7C85D;
}

#app {
  /*font-family: Sansation, Montserrat, Arial, sans-serif;*/

  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  scroll-behavior: smooth; 
}
@font-face {
  font-family: Montserrat;
  src: url('~@/assets/fonts/Montserrat-VariableFont_wght.ttf');
}

@font-face {
  font-family: Montserrat;
  src: url('~@/assets/fonts/Montserrat-Black.ttf');
  font-weight: 900;
}

@font-face {
  font-family: Sansation;
  src: url('~@/assets/fonts/Sansation_Regular.ttf');
  font-weight: 400;
}

@font-face {
  font-family: Sansation;
  src: url('~@/assets/fonts/Sansation_Bold.ttf');
  font-weight: 600;
}

@font-face {
  font-family: Sansation;
  src: url('~@/assets/fonts/Sansation_Light.ttf');
  font-weight: 300;
}

@import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap');

@import url('https://fonts.googleapis.com/css2?family=Cutive+Mono&display=swap');


html, body, #__nuxt {
  min-height: 100% !important;
  height: 100%;
  background-repeat: no-repeat;
  background-size: cover;
  background-color: #191F27;
  font-family: "Inter", sans-serif;
  color: #6D7785;
}

h1,h2,h3,h4,h5,h6 {
  font-family: Roboto;
  font-weight: 900;
  color:white;
}

h3,h4,h5,h6 {
  font-family: Roboto;
  font-weight: 500;
  color:white;
}

.jura {
  font-family: "Jura", sans-serif;
  font-style: normal;
}

.wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
}

.wrapper.user {
    background-color: #151a22;
}

.wrapper-content {
    flex: 1;
    min-height: 0;
    /* min-height: 100%; */
    display: flex;
    overflow: auto;
    overflow-x: clip;
    scroll-behavior: smooth;
    /* overflow-y: scroll; */
}

.wrapper.user .wrapper-content {
  background-color: #191f27;
  border-radius: 1rem 1rem 1rem 0;
  overflow: hidden;
}

.mbb-4 {
    margin-bottom: 4rem;
}

.mtt-5 {
    margin-top: 5rem;
}

.mtt-8 {
    margin-top: 8rem;
}

.mbb-5 {
    margin-bottom: 5rem;
}


.content {
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    /* padding-bottom: 1rem; */
    /* overflow: scroll; */
}

.bold {
  font-weight: bold;
}

.fill {
  flex: 1; 
  display: flex;
}

/* General form styling */

.form-check {
    display: flex;
}

.popup {
  position: absolute;
  bottom: 1rem;
  background: black;
  color: white;
  padding: 1rem;
  border-radius: 10px;
  right: 1rem;
}


.form-control, .form-select, .form-check-input[type="checkbox"] {
  background-color: #1F2631;
  border:none;
  outline:none;
  color:white;
  /*font-weight: bold;*/
  border-radius: 10px;
}

 .form-check-input[type="checkbox"] {
   padding:.7rem;
   border-radius: 6px;
   margin-right: 0.7rem;
 }

.form-control:active, .form-control:focus, .form-check-input[type="checkbox"] {
  background-color: #1F2631;
  outline: none;
  box-shadow: none;
  color:white;
}

 .form-control.dark, .form-control.dark:active, .form-check-input.dark, .form-control.dark:focus {
  background-color: #171C24;
 }

.form-floating > label {
    /*font-weight: bold;*/
    color: #6D7785;
    padding: 1.3rem 2rem;
}

.form-select > label {
    font-weight: bold;
    color: #6D7785;
    padding: 1.3rem 2rem;
}

.form-select:focus-visible {
  color: white;
  /* text-shadow: 0 0 0 #212529; */
  text-shadow: none;
}

.form-floating > .form-control {
  padding: 2rem 2rem;
  padding-left: 1.8rem;
}

.wrap {
  flex-wrap: wrap;
}

.form-floating > .form-select {
  padding-top: 1.2rem;
  padding-left: 1.6rem;
  padding-bottom: 0rem;
  height: 64px;
}

.form-floating > .form-control:focus, .form-floating > .form-control:not(:placeholder-shown) {
  padding-top: 2.5rem;
  padding-bottom: 1.5rem;
}

.form-check-label {
  line-height: 2rem;
  color: white;
}

.ws-gradient {
  background: linear-gradient(263.46deg, #4A97F2 0%, #C777FF 50%, #EC4777 100%);
}

.ws-gradient-text {
  background: linear-gradient(263.46deg, #4A97F2 0%, #B880FF 40.89%, #FE79A3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.roboto-thin {
  font-family: "Roboto", sans-serif;
  font-weight: 100;
  font-style: normal;
}

.roboto-light {
  font-family: "Roboto", sans-serif;
  font-weight: 300;
  font-style: normal;
}

.roboto-regular {
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-style: normal;
}

.roboto-medium {
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-style: normal;
}

.roboto-bold {
  font-family: "Roboto", sans-serif;
  font-weight: 700;
  font-style: normal;
}

.roboto-black {
  font-family: "Roboto", sans-serif;
  font-weight: 900;
  font-style: normal;
}

.roboto-thin-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 100;
  font-style: italic;
}

.roboto-light-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 300;
  font-style: italic;
}

.roboto-regular-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-style: italic;
}

.roboto-medium-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-style: italic;
}

.roboto-bold-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 700;
  font-style: italic;
}

.roboto-black-italic {
  font-family: "Roboto", sans-serif;
  font-weight: 900;
  font-style: italic;
}

a {
  text-decoration: none;
}

.alert {
  border-radius: 10px;
}

.box {
    background-color: #1E252F;
    border-radius: 10px;
    position: relative;
    overflow: hidden;
}


.box.seethrough {
    background-color: #1E252FCF;
  }

.box.padded {
    padding: 1.5rem 2rem;
}

.box.dark {
  background-color: #171C24;

}

.box.bordered {
  border: 1px solid #272E39;
}

.box.dark.bordered {
  border: 1px solid #232A35;
}


.box.small-padded {
  padding: .4rem 1.1rem;
}

.box.med-padded {
    padding: .7rem 1.5rem;
}

.box.vert-center {
  align-items: center;
}


.box.hori-center {
  justify-content: center;
}

.box.success {
  background-color: #334d37;
}

.white {
  color: white;
}

.box.squared {
  border-radius: 10px;
}

.box.bg-d {
  background-color: #191F27;
}

.box.bg-l {
  background-color: #222A35;
}

.box .header {
    padding: 1.5rem 2rem;
}

.box .element-header {
    display: flex;
    
  }

.box .element-header .main {
    color: white; 
    background-color: #2C3542;
    padding: .8rem 3rem .8rem 1.5rem;
    border-radius: 0 0 20px 0; 
    font-weight: bold;
    font-size: 1.1rem;
    z-index: 1;
  }

.element-header .sub {
    background-color: #232B37;
    padding: .8rem 3rem .8rem 3rem;
    border-radius: 0 0 20px 0; 
    left: -10px;
    position: relative;
  }

.box.clickable {
  cursor: pointer;
}

.box.clickable:hover {
    background-color: #262e3a;
}

.box.success:hover {
  background-color: #3e5c42;
}

.box.faded {
  opacity: .4;
}

.box.faded.clickable {
  cursor:pointer;
  transition: opacity ease .35s;
}

.box.faded.clickable:hover {
  opacity: .7;
}


.modal-header {
  padding: 1rem 1.5rem; 
  border-bottom: none;
}

.modal-footer {
  border-top: none;
}

.modal-content {
  background-color: #191F27;
  border-radius: 20px;
}

.modal-body {
    padding: 3rem;
}


.bg-360 {
  background-color: #5094ED;
}

.bg-public {
  background-color: #E780F5;
}

.bg-scan {
  background-color: #7357D2;
}

.dropdown-item {
    display: flex;
    align-items: center;
    padding: .5rem 2rem .5rem 1rem;
  border-radius: 10px;
}

.dropdown-item:hover {
    background-color: #eff3f7;
    /* background-color: transparent; */
}

.subheader {
    font-weight: 500;
    color: #536173;
    font-family: Sansation;
}

.box.transparent {
    background-color: transparent;
  }


.danger-bg {
  background-color: #9E4A4A; 
  stroke: #9E4A4A; 
  color: white;
}

.danger-bg-darker {
  background-color: #954242;
  stroke: #954242;
}

.warning-bg {
  background-color: #E7C85D;
  stroke: #E7C85D;
  color: white; 
}

.notice-bg {
  background-color: #46b8dd;
  color: white;
}

.good-bg {
  background-color: #5aa150;
  color:white;
}

.btn-continue {
    padding: .6rem 2rem;  
    background-color:#7357D2;
    font-weight: bold;
    outline:none;
    box-shadow: none;
    border:none;
    color: white;
    border-radius: 30px;
}

.btn-continue:hover {
    background-color: #7F62E3;
}

.btn-pink {
    padding: .6rem 2rem; 
    background-color:#E780F5;
    font-weight: bold;
    outline:none;
    box-shadow: none;
    border:none;
    color: white;
    border-radius: 30px;
}

.btn-pink:hover {
    background-color: #DA92E3;
    color:white;
}

.btn-blue {
    padding: .6rem 2rem; 
    background-color:#5094ED;
    font-weight: bold;
    outline:none;
    box-shadow: none;
    border:none;
    color: white;
    border-radius: 30px;
}

.btn-blue:hover {
    background-color: #63A1F2;
    color:white;
}

.alert-box {
  line-height: 1rem;
  background-color: #E75D5D;
  border-radius: 10px;
  font-weight: bold;
  color: white;
  padding: .2rem 0.6rem;
  display: flex;
  align-items: center;
  font-size: .9rem;
}

.centered {
  justify-content: center;
}

.column {
  flex-direction: column;
}
.box.drow {
  flex-direction: row;
}

.drow.centered {
  align-items: center
}


.column.centered {
  align-items: center;
}

.vertically-centered {
  align-items: center;
}

.column.vertically-centered {
  justify-content: center;
}


.warning-box {
  line-height: 1rem;
  background-color: #E7C85D;
  border-radius: 10px;
  font-weight: bold;
  color: white;
  padding: .2rem 0.6rem;
  display: flex;
  align-items: center;
  font-size: .9rem;
}

.info-box {
  line-height: 1rem;
  background-color: #5D62E7;
  border-radius: 10px;
  font-weight: bold;
  color: white;
  padding: .2rem 0.6rem;
  display: flex;
  align-items: center;
  font-size: .9rem;
}

.grayed {
    color: #C8D1DE;
}

/* DataTable styling */
.datatable tr {
  border-color: #2e3742;
  cursor: pointer;
}

.datatable td {
  color: white !important;
}

::-webkit-scrollbar {
  width: 20px;
  height: 20px;
  scroll-behavior: smooth;
}

::-webkit-scrollbar-track {
  background-color: transparent;
}

::-webkit-scrollbar-thumb {
  background-color: #4a5455;
  border-radius: 20px;
  border: 6px solid transparent;
  background-clip: content-box;
}

::-webkit-scrollbar-thumb:hover {
  background-color: #a8bbbf;
}

::-webkit-scrollbar-button { 
   display:none;
}

::-webkit-scrollbar-corner {
  background: transparent;
}

.hide-desktop {
  display: none;
}

@media (max-width: 600px) {
  .hide-mobile {
    display: none;
  }
  .hide-desktop {
    display: initial;
  }
}

.form-select:disabled {
  background-color: #1F2631;
  color: #ffffffba;
}

.uppercase {
  text-transform: uppercase;
}

/* Toast Notifications - Custom Dark Theme */
:deep(.Vue-Toastification__toast) {
  background-color: #1E252F !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 0.75rem !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5) !important;
  padding: 1rem 1.25rem !important;
  font-family: 'Inter', system-ui, -apple-system, sans-serif !important;
}

:deep(.Vue-Toastification__toast--success) {
  border-left: 4px solid #10B981 !important;
}

:deep(.Vue-Toastification__toast--error) {
  border-left: 4px solid #EF4444 !important;
}

:deep(.Vue-Toastification__toast--warning) {
  border-left: 4px solid #F59E0B !important;
}

:deep(.Vue-Toastification__toast--info) {
  border-left: 4px solid #3B82F6 !important;
}

:deep(.Vue-Toastification__toast-body) {
  color: #E5E7EB !important;
  font-size: 0.9rem !important;
  line-height: 1.5 !important;
}

:deep(.Vue-Toastification__progress-bar) {
  background: linear-gradient(90deg, rgba(168, 85, 247, 0.5), rgba(168, 85, 247, 0.8)) !important;
}

:deep(.Vue-Toastification__close-button) {
  color: #9CA3AF !important;
  opacity: 0.7 !important;
}

:deep(.Vue-Toastification__close-button:hover) {
  opacity: 1 !important;
  color: #E5E7EB !important;
}

:deep(.Vue-Toastification__icon) {
  width: 20px !important;
  margin-right: 0.75rem !important;
}
</style>
